export interface RoleBinding {
  readonly role: string;
  readonly siteId: string | null;
}

export interface CurrentUser {
  readonly id: string;
  readonly displayName: string | null;
  readonly bindings: readonly RoleBinding[];
  readonly siteId: string | null;
  readonly totpVerified: boolean;
  readonly locale: 'ru' | 'kk';
  /** Разрешено на выбранном объекте — по нему гейтятся кнопки. */
  readonly permissions: readonly string[];
  /** Разрешено хотя бы на одном из объектов роли — по нему строится меню. */
  readonly permissionsAnySite: readonly string[];
  /**
   * Пароль задан администратором и должен быть сменён владельцем.
   *
   * Приходит в `/auth/me`, а не только в ответе на вход: иначе флаг терялся
   * при первой же перезагрузке страницы и не значил ничего.
   */
  readonly mustChangePassword: boolean;
}

/** Проверка права на стороне интерфейса. Сервер проверяет то же самое ещё раз. */
export function allows(user: CurrentUser, ...actions: readonly string[]): boolean {
  return actions.some((action) => user.permissions.includes(action));
}

export function allowsAnywhere(user: CurrentUser, ...actions: readonly string[]): boolean {
  return actions.some((action) => user.permissionsAnySite.includes(action));
}

export interface SiteSummary {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly status: string;
}

export interface LoginResponse {
  readonly token: string;
  readonly needsTotp: boolean;
  /** Роль требует второго фактора, а он ещё не подключён — экран подключения. */
  readonly needsTotpEnrollment: boolean;
  readonly mustChangePassword: boolean;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

let siteId: string | null = null;

/*
 * Сессия админки — только в cookie `moika_session` (HttpOnly, Secure за
 * HTTPS). Раньше токен лежал в localStorage и уходил заголовком Bearer: любой
 * внедрённый скрипт читал его и уносил полную сессию администратора с
 * пройденным вторым фактором (обжарка R-S11). Скрипт cookie HttpOnly не видит.
 * Токен из прошлых версий в localStorage стирается при загрузке.
 */
try {
  localStorage.removeItem('moika_token');
} catch {
  // Хранилище недоступно — стирать нечего.
}

/**
 * Заголовок против подделки запроса со стороннего сайта. Сессия теперь в
 * cookie, и сервер принимает её на изменяющих запросах только с этим
 * заголовком: чужая страница выставить его без разрешения CORS не может.
 */
export const CSRF_HEADER = 'x-moika-csrf';

export function sessionHeaders(): Record<string, string> {
  const headers: Record<string, string> = { [CSRF_HEADER]: '1' };
  const site = storedSite();
  if (site) headers['x-moika-site'] = site;
  return headers;
}

export function setSite(value: string | null): void {
  siteId = value;
  if (value) localStorage.setItem('moika_site', value);
  else localStorage.removeItem('moika_site');
}

/**
 * Список объектов в шапке грузится один раз при входе. Раздел «Объекты» после
 * заведения или правки объекта сообщает об этом оболочке, и та перечитывает
 * список: иначе новый объект появлялся в выборе только после F5 (QA.md, Q19).
 */
export const SITES_CHANGED_EVENT = 'moika:sites-changed';

export function announceSitesChanged(): void {
  window.dispatchEvent(new Event(SITES_CHANGED_EVENT));
}

export function storedSite(): string | null {
  return siteId ?? localStorage.getItem('moika_site');
}

export interface RequestOptions {
  /**
   * Ключ идемпотентности для операций с деньгами. Сервер требует его на
   * инкассации, оплате и списаниях: повторное нажатие кнопки не должно
   * приводить ко второму движению средств.
   */
  readonly idempotencyKey?: string;
}

export function newIdempotencyKey(scope: string): string {
  const random =
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${scope}:${random}`;
}

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json', ...sessionHeaders() };
  if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;

  const res = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const text = await res.text();
  const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!res.ok) {
    throw new ApiError(
      String(payload['error'] ?? 'E00.internal'),
      String(payload['message'] ?? 'Ошибка запроса'),
      res.status,
    );
  }
  return payload['data'] as T;
}

export async function login(
  loginName: string,
  password: string,
): Promise<LoginResponse> {
  const data = await request<LoginResponse>('POST', '/api/v1/auth/login', {
    login: loginName,
    password,
  });
  // Токен в ответе нужен устройствам и сценариям; админка работает по cookie
  // и у себя его не хранит.
  return data;
}

export async function verifyTotp(code: string): Promise<void> {
  await request<{ verified: boolean }>('POST', '/api/v1/auth/totp/verify', { code });
}

/** Подключение второго фактора самим сотрудником при первом входе. */
export function enrollTotp(): Promise<{ secret: string; uri: string }> {
  return request('POST', '/api/v1/auth/totp/enroll');
}

export async function confirmTotpEnrollment(code: string): Promise<void> {
  await request<{ verified: boolean }>('POST', '/api/v1/auth/totp/confirm', { code });
}

export async function me(): Promise<CurrentUser> {
  const user = await request<CurrentUser>('GET', '/api/v1/auth/me');
  // Старый сервер мог не прислать списки — тогда интерфейс не должен прятать всё.
  return {
    ...user,
    permissions: user.permissions ?? [],
    permissionsAnySite: user.permissionsAnySite ?? user.permissions ?? [],
    mustChangePassword: user.mustChangePassword ?? false,
  };
}

export async function sites(): Promise<readonly SiteSummary[]> {
  return request<readonly SiteSummary[]>('GET', '/api/v1/sites');
}

export async function logout(): Promise<void> {
  try {
    await request<{ revoked: boolean }>('POST', '/api/v1/auth/logout');
  } finally {
    setSite(null);
  }
}
